// TypeScript
// Enhanced composable: SSR-safe, idempotent, Promise-cached loader, internal Map tracking, optional DOM attribute set for compatibility.
import { appEnv } from 'config/app'

type TurnstileAPI = {
  render: (selector: string, opts: Record<string, any>) => string
  getResponse: (widgetId: string) => string | null
  reset?: (widgetId?: string) => void
}

const TEST_SITE_KEY = '1x00000000000000000000AA'

function resolveSiteKey(explicit?: string) {
  // Prefer explicit, then configured env key from appEnv, then test key in dev
  if (explicit) return explicit
  const envKey = appEnv.turnstileSiteKey
  if (envKey && envKey.trim().length > 0) return envKey.trim()
  return import.meta.env.DEV ? TEST_SITE_KEY : '' // prod without key should be empty -> fail fast
}

export function useTurnstile() {
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'

  const state: {
    scriptLoaded: boolean
    loadingPromise?: Promise<void>
    // Track containerId -> widgetId
    widgets: Map<string, string>
  } = (globalThis as any).__useTurnstileState || {
    scriptLoaded: isBrowser && typeof (window as any).turnstile !== 'undefined',
    loadingPromise: undefined,
    widgets: new Map<string, string>()
  }
  ;(globalThis as any).__useTurnstileState = state

  const loadScript = () => {
    if (!isBrowser) return Promise.resolve() // SSR no-op
    if (state.scriptLoaded) return Promise.resolve()
    if (state.loadingPromise) return state.loadingPromise

    state.loadingPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>(
        'script[src*="challenges.cloudflare.com/turnstile"]'
      )
      if (existing) {
        if ((window as any).turnstile) {
          state.scriptLoaded = true
          resolve()
          return
        }
        const t = setInterval(() => {
          if (typeof window === 'undefined') {
            clearInterval(t)
            return
          }
          if ((window as any).turnstile) {
            clearInterval(t)
            state.scriptLoaded = true
            resolve()
          }
        }, 100)
        setTimeout(() => {
          clearInterval(t)
          if (typeof window === 'undefined') return
          if ((window as any).turnstile) {
            state.scriptLoaded = true
            resolve()
          } else {
            reject(new Error('Turnstile script present but window.turnstile did not initialize'))
          }
        }, 5000)
        return
      }

      const script = document.createElement('script')
      // Explicit rendering provides full control and stable callbacks
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = () => {
        const start = Date.now()
        const check = () => {
          if ((window as any).turnstile) {
            state.scriptLoaded = true
            resolve()
          } else if (Date.now() - start > 5000) {
            reject(new Error('Turnstile loaded but did not initialize'))
          } else {
            setTimeout(check, 50)
          }
        }
        check()
      }
      script.onerror = (e) => reject(e as any)
      document.head.appendChild(script)
    })
    return state.loadingPromise
  }

  type RenderOptions = {
    sitekey?: string
    theme?: 'auto' | 'light' | 'dark'
    appearance?: 'always' | 'execute' | 'interaction-only'
    retry?: 'auto' | 'never'
    'refresh-expired'?: 'auto' | 'manual'
    callback?: (token: string) => void
    'expired-callback'?: () => void
    'error-callback'?: (code: string) => void
    // Any additional options Cloudflare supports
    [k: string]: any
  }

  const renderOnce = async (
    containerId: string,
    sitekey?: string,
    options?: RenderOptions
  ): Promise<{ widgetId: string | null; alreadyRendered: boolean }> => {
    if (!isBrowser) throw new Error('Turnstile cannot render during SSR')

    // Prevent double-render for same container via internal Map
    const existingWidFromMap = state.widgets.get(containerId)
    if (existingWidFromMap) {
      return { widgetId: existingWidFromMap, alreadyRendered: true }
    }

    // Genuine misuse: the container was never mounted
    if (!document.getElementById(containerId)) {
      throw new Error(`Turnstile container "${containerId}" not found`)
    }

    try {
      await loadScript()
    } catch (err) {
      if (!document.getElementById(containerId)) {
        // Container unmounted while the script was loading (e.g. the user
        // navigated away) — a benign race, treat as a cancelled render
        return { widgetId: null, alreadyRendered: false }
      }
      throw err
    }

    const container = document.getElementById(containerId)
    if (!container) {
      // Same benign race, with the script load having succeeded
      return { widgetId: null, alreadyRendered: false }
    }

    const resolvedSiteKey = resolveSiteKey(sitekey)
    if (!resolvedSiteKey) {
      // In production, absence of a site key should be surfaced clearly
      throw new Error(
        'Turnstile site key missing. Set VITE_TURNSTILE_SITE_KEY in the environment for production.'
      )
    }

    const api: TurnstileAPI | undefined = (window as any).turnstile
    if (!api) throw new Error('window.turnstile is not available after loading script')

    const wid = api.render(`#${containerId}`, {
      sitekey: resolvedSiteKey,
      theme: options?.theme ?? 'auto',
      appearance: options?.appearance ?? 'always',
      retry: options?.retry ?? 'auto',
      'refresh-expired': options?.['refresh-expired'] ?? 'auto',
      callback: options?.callback,
      'expired-callback': options?.['expired-callback'],
      'error-callback': options?.['error-callback'],
      ...options
    })

    if (wid) {
      state.widgets.set(containerId, String(wid))
      // Backward compatibility: expose widget id as data attribute for existing code paths
      try {
        container.setAttribute('data-turnstile-widget-id', String(wid))
      } catch {
        // ignore: attribute set is best-effort for backward compatibility
      }
    }

    return { widgetId: wid ?? null, alreadyRendered: false }
  }

  const getResponse = (widgetId: string) => {
    const api: TurnstileAPI | undefined = (isBrowser ? (window as any).turnstile : undefined)
    if (!api) return null
    try {
      return api.getResponse(widgetId)
    } catch {
      return null
    }
  }

  const reset = (widgetId?: string) => {
    const api: TurnstileAPI | undefined = (isBrowser ? (window as any).turnstile : undefined)
    if (!api || typeof api.reset !== 'function') return
    try {
      api.reset(widgetId)
    } catch {
      // ignore
    }
  }

  /**
   * Tear down a rendered widget. Takes the CONTAINER id (or element) — the
   * same id passed to renderOnce — NOT the Cloudflare widget id. Clears the
   * internal registry entry so renderOnce can re-render on the next mount;
   * safe to call when nothing was rendered.
   */
  const destroy = (container: string | Element) => {
    if (!isBrowser) return
    const id = typeof container === 'string' ? container : container.id
    if (!id) return
    state.widgets.delete(id)
    const el = typeof container === 'string' ? document.getElementById(container) : (container as Element)
    if (el) {
      // remove any rendered child nodes (iframe etc.)
      try {
        ;(el as HTMLElement).innerHTML = ''
        el.removeAttribute('data-turnstile-widget-id')
      } catch {
        // ignore: DOM cleanup best-effort
      }
    }
  }

  return { loadScript, renderOnce, getResponse, reset, destroy, _state: state }
}
